import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { State } from '../../../reducer';

const AdminTutorial = () => {
    const user = useSelector((state: State) => state.user);
    const location = useLocation();
    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);
    const [tutorialKey, setTutorialKey] = useState<string>('');

    useEffect(() => {
        // Determine which tutorial to show based on the current path
        const path = location.pathname;
        let currentSteps: Step[] = [];
        let currentKey = '';

        if (path.includes('/dashboard-admin')) {
            // Home / Dashboard Tutorial
            currentKey = 'tutorial_seen_home';
            currentSteps = [
                {
                    target: 'body',
                    content: (
                        <div className="text-center">
                            <h2 className="text-xl font-bold mb-2">Welcome to Your Admin Dashboard! 🏠</h2>
                            <p>This is your command center. Let's take a quick look around.</p>
                        </div>
                    ),
                    placement: 'center',
                    disableBeacon: true,
                },
                {
                    target: '#tutorial-nav-user-management',
                    content: 'Manage your team, assign roles, and handle user access here.',
                },
                {
                    target: '#tutorial-nav-stats',
                    content: 'Track cafe performance and view detailed statistics.',
                },
            ];
        } else if (path.includes('/userManagement')) {
            // User Management Tutorial
            currentKey = 'tutorial_seen_user_mgmt';
            currentSteps = [
                {
                    target: 'body',
                    content: 'This is the User Management page. Here you can control who has access to what.',
                    placement: 'center',
                    disableBeacon: true,
                },
                {
                    target: '#tutorial-search-bar',
                    content: 'Looking for someone specific? Use the search bar to filter by name or email.',
                },
                {
                    target: '.tutorial-edit-btn',
                    content: 'Need to promote a Barista or Admin? Click this edit icon to change a user\'s role.',
                },
            ];
        } else if (path.includes('/dashboard-stats-admin')) {
            // Statistics Tutorial
            currentKey = 'tutorial_seen_stats';
            // Assuming there are elements on this page we can target, or just a general overview
            // Since we haven't added specific IDs to the stats page content yet, we'll keep it general or target the body
            currentSteps = [
                {
                    target: 'body',
                    content: (
                        <div className="text-center">
                            <h2 className="text-xl font-bold mb-2">Statistics Dashboard 📈</h2>
                            <p>Gain insights into your sales, popular items, and peak hours here.</p>
                        </div>
                    ),
                    placement: 'center',
                    disableBeacon: true,
                }
            ];
        }

        if (currentKey && currentSteps.length > 0 && user?.role === 'admin') {
            const seen = localStorage.getItem(currentKey);
            if (!seen) {
                setSteps(currentSteps);
                setTutorialKey(currentKey);
                // Add a small delay to ensure elements are rendered
                const timer = setTimeout(() => setRun(true), 500);
                return () => clearTimeout(timer);
            }
        } else {
            setRun(false); // Stop if no valid tutorial for this route or already seen
        }
    }, [location.pathname, user]);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setRun(false);
            if (tutorialKey) {
                localStorage.setItem(tutorialKey, 'true');
            }
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            continuous
            showProgress
            showSkipButton
            callback={handleJoyrideCallback}
            styles={{
                options: {
                    zIndex: 10000,
                    primaryColor: '#3B82F6',
                    backgroundColor: '#ffffff',
                    overlayColor: 'rgba(0, 0, 0, 0.5)',
                    textColor: '#333333',
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                buttonNext: {
                    backgroundColor: '#3B82F6',
                }
            }}
            locale={{
                last: "Got it!",
                skip: "Skip"
            }}
        />
    );
};

export default AdminTutorial;
